import styled, { keyframes } from "styled-components";

const fadeUp = keyframes`
  from {
    opacity: 0;
    transform: translateY(8px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
`;

export const BannerSection = styled.div`
  grid-column: 1 / -1;
  width: 100%;
  max-width: 100%;
  box-sizing: border-box;
  margin-top: 8px;
  animation: ${fadeUp} 0.5s ease-out both;

  /* Mobile estreito: evita grudar nas bordas laterais do grid/lista */
  @media (max-width: 39.9375rem) {
    justify-self: center;
    width: calc(100% - 20px);
    max-width: calc(100% - 20px);
    margin-left: 10px;
    margin-right: 10px;
  }

  @media (min-width: ${({ theme }) => theme.breakpoints.tablet}) {
    margin-top: 12px;
  }

  /* Somente telas grandes: afasta do rodapé da área de listagem */
  @media (min-width: ${({ theme }) => theme.breakpoints.desktop}) {
    margin-bottom: 36px;
  }
`;

export const BannerCard = styled.div`
  position: relative;
  overflow: hidden;
  border-radius: 16px;
  padding: 22px 20px;
  background: linear-gradient(
    135deg,
    ${({ theme }) => theme.palette.primary.blue50} 0%,
    #ffffff 48%,
    ${({ theme }) => theme.palette.secondary.orange50} 100%
  );
  border: 1px solid ${({ theme }) => theme.palette.primary.blue100};
  box-shadow: 0 4px 24px rgba(61, 91, 140, 0.08);

  &::before {
    content: "";
    position: absolute;
    top: -40%;
    right: -10%;
    width: 180px;
    height: 180px;
    border-radius: 50%;
    background: radial-gradient(
      circle,
      ${({ theme }) => theme.palette.primary.blue100}33 0%,
      transparent 70%
    );
    pointer-events: none;
  }

  @media (min-width: ${({ theme }) => theme.breakpoints.mobileSm}) {
    padding: 26px 24px;
  }

  @media (min-width: ${({ theme }) => theme.breakpoints.tablet}) {
    padding: 28px 32px;
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 24px;
  }
`;

export const BannerInner = styled.div`
  position: relative;
  z-index: 1;
  display: flex;
  flex-direction: column;
  gap: 16px;

  @media (min-width: ${({ theme }) => theme.breakpoints.tablet}) {
    flex-direction: row;
    align-items: center;
    flex: 1;
    gap: 20px;
  }
`;

export const BannerIconWrap = styled.div`
  flex-shrink: 0;
  width: 52px;
  height: 52px;
  border-radius: 14px;
  display: flex;
  align-items: center;
  justify-content: center;
  background: linear-gradient(
    145deg,
    ${({ theme }) => theme.palette.secondary.orange100},
    ${({ theme }) => theme.palette.secondary.orange50}
  );
  color: ${({ theme }) => theme.palette.secondary.orange600};
  box-shadow: 0 2px 12px ${({ theme }) => theme.palette.secondary.orange200}88;

  @media (min-width: ${({ theme }) => theme.breakpoints.tablet}) {
    width: 56px;
    height: 56px;
  }
`;

export const BannerTextBlock = styled.div`
  flex: 1;
  min-width: 0;
`;

export const BannerTitle = styled.h3`
  margin: 0 0 8px 0;
  font-family: "Plus Jakarta Sans", sans-serif;
  font-size: ${18 / 16}rem;
  font-weight: 700;
  color: ${({ theme }) => theme.palette.primary.blue900};
  line-height: 1.25;

  @media (min-width: ${({ theme }) => theme.breakpoints.tablet}) {
    font-size: ${20 / 16}rem;
    margin-bottom: 6px;
  }
`;

export const BannerSubtitle = styled.p`
  margin: 0;
  font-size: ${14 / 16}rem;
  line-height: 1.5;
  color: ${({ theme }) => theme.palette.greyscale.grey600};

  @media (min-width: ${({ theme }) => theme.breakpoints.tablet}) {
    font-size: ${15 / 16}rem;
    max-width: 36rem;
  }
`;

export const BannerCta = styled.button`
  position: relative;
  z-index: 1;
  margin-top: 4px;
  align-self: flex-start;
  border: none;
  cursor: pointer;
  font-family: "Plus Jakarta Sans", sans-serif;
  font-size: ${15 / 16}rem;
  font-weight: 600;
  padding: 12px 22px;
  border-radius: 10px;
  color: #fff;
  background: linear-gradient(
    135deg,
    ${({ theme }) => theme.palette.secondary.orange500},
    ${({ theme }) => theme.palette.secondary.orange600}
  );
  box-shadow: 0 4px 14px ${({ theme }) => theme.palette.secondary.orange400}66;
  transition: transform 0.2s ease, box-shadow 0.2s ease;

  &:hover {
    transform: translateY(-1px);
    box-shadow: 0 6px 20px ${({ theme }) => theme.palette.secondary.orange500}55;
  }

  &:active {
    transform: translateY(0);
  }

  &:focus-visible {
    outline: 2px solid ${({ theme }) => theme.palette.primary.blue400};
    outline-offset: 3px;
  }

  @media (min-width: ${({ theme }) => theme.breakpoints.tablet}) {
    margin-top: 0;
    flex-shrink: 0;
    padding: 14px 26px;
  }
`;

export const ModalOverlay = styled.div`
  position: fixed;
  inset: 0;
  z-index: 10050;
  background: rgba(23, 30, 48, 0.55);
  backdrop-filter: blur(4px);
  display: flex;
  align-items: flex-end;
  justify-content: center;
  box-sizing: border-box;
  /* Mobile: margem lateral + safe areas */
  padding: 0 14px;
  padding-bottom: max(12px, env(safe-area-inset-bottom, 0px));
  padding-left: max(14px, env(safe-area-inset-left, 0px));
  padding-right: max(14px, env(safe-area-inset-right, 0px));

  @media (min-width: ${({ theme }) => theme.breakpoints.mobileSm}) {
    align-items: center;
    padding: 16px;
    padding-bottom: max(16px, env(safe-area-inset-bottom, 0px));
    padding-left: max(16px, env(safe-area-inset-left, 0px));
    padding-right: max(16px, env(safe-area-inset-right, 0px));
  }
`;

export const ModalPanel = styled.div`
  width: 100%;
  max-width: min(480px, 100%);
  max-height: min(92vh, 720px);
  overflow: hidden;
  display: flex;
  flex-direction: column;
  background: #fff;
  border-radius: 20px 20px 0 0;
  box-shadow: 0 -8px 40px rgba(0, 0, 0, 0.12);
  box-sizing: border-box;

  @media (min-width: ${({ theme }) => theme.breakpoints.mobileSm}) {
    border-radius: 20px;
    max-height: min(90vh, 680px);
    box-shadow: 0 24px 48px rgba(23, 30, 48, 0.2);
  }
`;

export const ModalHeader = styled.div`
  flex-shrink: 0;
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 12px;
  padding: 20px 20px 12px;
  border-bottom: 1px solid ${({ theme }) => theme.palette.greyscale.grey50};

  @media (min-width: ${({ theme }) => theme.breakpoints.tablet}) {
    padding: 24px 24px 16px;
  }
`;

export const ModalHeaderLeft = styled.div`
  display: flex;
  align-items: flex-start;
  gap: 14px;
  min-width: 0;
`;

export const ModalHeaderIcon = styled.div`
  width: 44px;
  height: 44px;
  border-radius: 12px;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  background: ${({ theme }) => theme.palette.primary.blue50};
  color: ${({ theme }) => theme.palette.primary.blue600};
`;

export const ModalTitle = styled.h2`
  margin: 0;
  font-family: "Plus Jakarta Sans", sans-serif;
  font-size: ${18 / 16}rem;
  font-weight: 700;
  color: ${({ theme }) => theme.palette.black.default};
  line-height: 1.3;

  @media (min-width: ${({ theme }) => theme.breakpoints.tablet}) {
    font-size: ${20 / 16}rem;
  }
`;

export const ModalSubtitle = styled.p`
  margin: 6px 0 0 0;
  font-size: ${13 / 16}rem;
  line-height: 1.45;
  color: ${({ theme }) => theme.palette.greyscale.grey600};
`;

export const ModalClose = styled.button`
  flex-shrink: 0;
  width: 40px;
  height: 40px;
  border: none;
  border-radius: 10px;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  color: ${({ theme }) => theme.palette.greyscale.grey500};
  background: transparent;
  transition: background 0.15s ease, color 0.15s ease;

  &:hover {
    background: ${({ theme }) => theme.palette.greyscale.grey50};
    color: ${({ theme }) => theme.palette.secondary.orange500};
  }

  &:focus-visible {
    outline: 2px solid ${({ theme }) => theme.palette.primary.blue300};
    outline-offset: 2px;
  }
`;

export const ModalBody = styled.div`
  flex: 1;
  overflow-y: auto;
  padding: 16px 20px 20px;
  -webkit-overflow-scrolling: touch;

  @media (min-width: ${({ theme }) => theme.breakpoints.tablet}) {
    padding: 16px 24px 24px;
  }
`;

export const FieldGroup = styled.div`
  margin-bottom: 18px;

  &:last-of-type {
    margin-bottom: 0;
  }
`;

export const FieldLabel = styled.label`
  display: block;
  margin-bottom: 8px;
  font-size: ${13 / 16}rem;
  font-weight: 600;
  color: ${({ theme }) => theme.palette.greyscale.grey700};
`;

export const FieldHint = styled.span`
  font-weight: 400;
  color: ${({ theme }) => theme.palette.greyscale.grey500};
`;

export const TextInput = styled.input<{ $invalid?: boolean }>`
  width: 100%;
  box-sizing: border-box;
  padding: 12px 14px;
  font-size: ${15 / 16}rem;
  border-radius: 10px;
  border: 1px solid
    ${({ theme, $invalid }) =>
      $invalid ? theme.palette.error.red : theme.palette.greyscale.grey200};
  background: ${({ theme }) => theme.palette.greyscale.grey50};
  color: ${({ theme }) => theme.palette.black.default};
  transition: border-color 0.15s ease, box-shadow 0.15s ease;

  &::placeholder {
    color: ${({ theme }) => theme.palette.greyscale.grey400};
  }

  &:hover {
    border-color: ${({ theme, $invalid }) =>
      $invalid ? theme.palette.error.red : theme.palette.greyscale.grey300};
  }

  &:focus {
    outline: none;
    border-color: ${({ theme, $invalid }) =>
      $invalid ? theme.palette.error.red : theme.palette.primary.blue400};
    box-shadow: 0 0 0 3px
      ${({ theme, $invalid }) =>
        $invalid ? "rgba(255, 56, 56, 0.15)" : theme.palette.primary.blue100};
    background: #fff;
  }
`;

export const KindToggleRow = styled.div`
  display: flex;
  gap: 10px;
`;

export const KindOption = styled.button<{ $active: boolean }>`
  flex: 1;
  padding: 12px 14px;
  border-radius: 10px;
  font-size: ${14 / 16}rem;
  font-weight: 600;
  font-family: inherit;
  cursor: pointer;
  border: 2px solid
    ${({ $active, theme }) =>
      $active ? theme.palette.primary.blue400 : theme.palette.greyscale.grey200};
  background: ${({ $active, theme }) =>
    $active ? theme.palette.primary.blue50 : "#fff"};
  color: ${({ $active, theme }) =>
    $active ? theme.palette.primary.blue800 : theme.palette.greyscale.grey600};
  transition: border-color 0.15s ease, background 0.15s ease, color 0.15s ease;

  &:hover {
    border-color: ${({ theme }) => theme.palette.primary.blue300};
  }

  &:focus-visible {
    outline: 2px solid ${({ theme }) => theme.palette.primary.blue300};
    outline-offset: 2px;
  }
`;

export const RowTwoCols = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 12px;

  @media (max-width: 380px) {
    grid-template-columns: 1fr;
  }
`;

export const NotifySection = styled.div`
  margin-top: 4px;
  padding-top: 18px;
  border-top: 1px dashed ${({ theme }) => theme.palette.greyscale.grey200};
`;

export const NotifySectionTitle = styled.p`
  margin: 0 0 12px 0;
  font-size: ${13 / 16}rem;
  font-weight: 600;
  color: ${({ theme }) => theme.palette.greyscale.grey700};
`;

export const CheckboxRow = styled.label`
  display: flex;
  align-items: flex-start;
  gap: 10px;
  margin-bottom: 12px;
  cursor: pointer;
  font-size: ${14 / 16}rem;
  line-height: 1.4;
  color: ${({ theme }) => theme.palette.greyscale.grey700};

  &:last-child {
    margin-bottom: 0;
  }
`;

export const CheckboxInput = styled.input`
  margin-top: 3px;
  width: 18px;
  height: 18px;
  flex-shrink: 0;
  accent-color: ${({ theme }) => theme.palette.secondary.orange500};
  cursor: pointer;
`;

export const CheckboxText = styled.span`
  flex: 1;
  min-width: 0;
`;

export const SelectWrapper = styled.div`
  position: relative;
  width: 100%;
`;

export const SelectNative = styled.select<{ $invalid?: boolean }>`
  width: 100%;
  box-sizing: border-box;
  padding: 12px 44px 12px 14px;
  font-size: ${15 / 16}rem;
  font-family: inherit;
  line-height: 1.35;
  border-radius: 10px;
  border: 1px solid
    ${({ theme, $invalid }) =>
      $invalid ? theme.palette.error.red : theme.palette.greyscale.grey200};
  background: ${({ theme }) => theme.palette.greyscale.grey50};
  color: ${({ theme }) => theme.palette.black.default};
  cursor: pointer;
  appearance: none;
  -webkit-appearance: none;
  -moz-appearance: none;

  &:hover {
    border-color: ${({ theme, $invalid }) =>
      $invalid ? theme.palette.error.red : theme.palette.greyscale.grey300};
  }

  &:focus {
    outline: none;
    border-color: ${({ theme, $invalid }) =>
      $invalid ? theme.palette.error.red : theme.palette.primary.blue400};
    box-shadow: 0 0 0 3px
      ${({ theme, $invalid }) =>
        $invalid ? "rgba(255, 56, 56, 0.15)" : theme.palette.primary.blue100};
    background: #fff;
  }
`;

export const SelectChevron = styled.span`
  position: absolute;
  right: 16px;
  top: 50%;
  transform: translateY(-50%);
  pointer-events: none;
  color: ${({ theme }) => theme.palette.greyscale.grey500};
  display: flex;
  align-items: center;
  justify-content: center;
  width: 22px;
  height: 22px;
`;

export const SearchContextHint = styled.p`
  margin: 10px 0 0 0;
  padding: 0 2px 0 0;
  font-size: ${12 / 16}rem;
  color: ${({ theme }) => theme.palette.greyscale.grey500};
  line-height: 1.45;
`;

export const FieldError = styled.p`
  margin: 6px 0 0 0;
  font-size: ${12 / 16}rem;
  line-height: 1.35;
  color: ${({ theme }) => theme.palette.error.red};
`;

export const ErrorText = styled.p`
  margin: 12px 0 0 0;
  font-size: ${13 / 16}rem;
  color: ${({ theme }) => theme.palette.error.red};
`;

export const SuccessText = styled.p`
  margin: 12px 0 0 0;
  font-size: ${13 / 16}rem;
  color: ${({ theme }) => theme.palette.primary.blue700};
`;

export const ModalFooter = styled.div`
  flex-shrink: 0;
  display: flex;
  flex-direction: column-reverse;
  gap: 10px;
  padding: 16px 20px 20px;
  background: #fff;
  border-top: 1px solid ${({ theme }) => theme.palette.greyscale.grey50};

  @media (min-width: ${({ theme }) => theme.breakpoints.mobileXs}) {
    flex-direction: row;
    justify-content: flex-end;
  }

  @media (min-width: ${({ theme }) => theme.breakpoints.tablet}) {
    padding: 16px 24px 24px;
  }
`;

export const FooterButtonSecondary = styled.button`
  padding: 12px 20px;
  border-radius: 10px;
  font-size: ${15 / 16}rem;
  font-weight: 600;
  font-family: inherit;
  cursor: pointer;
  border: 1px solid ${({ theme }) => theme.palette.greyscale.grey200};
  background: #fff;
  color: ${({ theme }) => theme.palette.greyscale.grey700};
  transition: background 0.15s ease, border-color 0.15s ease;

  &:hover {
    background: ${({ theme }) => theme.palette.greyscale.grey50};
    border-color: ${({ theme }) => theme.palette.greyscale.grey300};
  }

  &:focus-visible {
    outline: 2px solid ${({ theme }) => theme.palette.primary.blue300};
    outline-offset: 2px;
  }
`;

export const FooterButtonPrimary = styled.button`
  padding: 12px 22px;
  border-radius: 10px;
  font-size: ${15 / 16}rem;
  font-weight: 600;
  font-family: inherit;
  cursor: pointer;
  border: 1px solid ${({ theme }) => theme.palette.secondary.orange500};
  background: #fff;
  color: ${({ theme }) => theme.palette.secondary.orange600};
  transition: background 0.15s ease, border-color 0.15s ease, color 0.15s ease;

  &:hover:not(:disabled) {
    background: ${({ theme }) => theme.palette.secondary.orange50};
    border-color: ${({ theme }) => theme.palette.secondary.orange600};
  }

  &:disabled {
    opacity: 0.55;
    cursor: not-allowed;
  }

  &:focus-visible {
    outline: 2px solid ${({ theme }) => theme.palette.primary.blue400};
    outline-offset: 2px;
  }
`;
